<template>
  <div class="announcements-page">
    <div class="row">
      <div class="col-12">
        <card>
          <!-- Filter button group -->
          <div class="row mb-3">
            <div class="col">
              <div class="btn-group btn-group-toggle float-left" data-toggle="buttons">
                <label
                  v-for="tab in filterTabs"
                  :key="tab.value"
                  class="btn btn-sm btn-primary btn-simple"
                  :class="{ active: activeFilter === tab.value }"
                >
                  <input
                    type="radio"
                    name="filter"
                    autocomplete="off"
                    :checked="activeFilter === tab.value"
                    @click="setFilter(tab.value)"
                  />
                  <span style="font-size: medium; font-weight: lighter">{{ tab.label }}{{ tab.value === 'unread' && unreadCount > 0 ? ` (${unreadCount})` : '' }}</span>
                </label>
              </div>
              <base-button
                v-if="unreadCount > 0"
                type="primary"
                size="sm"
                class="float-right"
                @click="handleMarkAllRead"
              >
                Mark all as read
              </base-button>
            </div>
          </div>

          <!-- Loading state -->
          <loading-panel v-if="loading" />

          <template v-else>
            <!-- Empty state -->
            <div v-if="announcements.length === 0" class="mt-3">
              <div class="font-icon-detail">
                <span style="font-size: 3em">
                  <i class="tim-icons icon-bell-55"></i>
                </span>
                <h4 class="mt-3">No announcements</h4>
              </div>
            </div>

            <!-- Announcements list -->
            <div v-else class="announcements-list">
              <div
                v-for="announcement in announcements"
                :key="announcement.id"
                class="announcement-card"
                :class="{
                  unread: !announcement.is_read,
                  expired: announcement.is_expired
                }"
              >
                <div class="priority-border" :class="'priority-' + announcement.priority"></div>

                <div class="announcement-body">
                  <div class="announcement-header d-flex justify-content-between align-items-start">
                    <div>
                      <span class="priority-badge" :class="'badge-' + announcement.priority">
                        {{ announcement.priority }}
                      </span>
                      <span v-if="announcement.is_expired" class="expired-badge ml-2">
                        Expired
                      </span>
                      <span v-if="!announcement.is_read" class="unread-badge ml-2">
                        New
                      </span>
                    </div>
                    <div class="announcement-date">
                      {{ formatDate(announcement.created_at) }}
                    </div>
                  </div>

                  <h4 class="announcement-title mt-2">{{ announcement.title }}</h4>

                  <div class="announcement-content mt-3">
                    <editor-js-renderer :content="announcement.content" />
                  </div>

                  <div class="announcement-actions mt-3" v-if="!announcement.is_read">
                    <base-button
                      type="default"
                      size="sm"
                      @click="handleDismiss(announcement)"
                    >
                      Dismiss
                    </base-button>
                    <base-button
                      type="primary"
                      size="sm"
                      class="ml-2"
                      @click="handleMarkAsRead(announcement)"
                    >
                      Mark as read
                    </base-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <b-pagination
              v-if="totalRows > perPage"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              @input="onPageChange"
              class="mt-4"
            ></b-pagination>
          </template>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import { Card, BaseButton } from '@/components';
import { BPagination } from 'bootstrap-vue';
import LoadingPanel from '@/components/LoadingPanel';
import EditorJsRenderer from '@/components/Announcements/EditorJsRenderer';
import announcementsApi from '@/services/announcementsApi';

export default {
  name: 'AnnouncementsPage',

  components: {
    Card,
    BaseButton,
    BPagination,
    LoadingPanel,
    EditorJsRenderer
  },

  data() {
    return {
      loading: true,
      announcements: [],
      unreadCount: 0,
      activeFilter: 'all',
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filterTabs: [
        { label: 'All', value: 'all', icon: 'tim-icons icon-bullet-list-67' },
        { label: 'Unread', value: 'unread', icon: 'tim-icons icon-bell-55' },
        { label: 'Read', value: 'read', icon: 'tim-icons icon-check-2' }
      ]
    };
  },

  mounted() {
    this.fetchAnnouncements();
    this.fetchUnreadCount();
  },

  methods: {
    getOffset() {
      return (this.currentPage * this.perPage) - this.perPage;
    },

    async fetchAnnouncements() {
      this.loading = true;
      try {
        const options = {
          status: this.activeFilter,
          limit: this.perPage,
          offset: this.getOffset()
        };

        const response = await announcementsApi.getAnnouncements(options);
        this.announcements = response.data.announcements || [];
        this.totalRows = response.data.pagination?.total_count || 0;
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to load announcements',
          timeout: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchUnreadCount() {
      try {
        const response = await announcementsApi.getUnreadCount();
        this.unreadCount = response.data.count || 0;
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    },

    setFilter(filter) {
      this.activeFilter = filter;
      this.currentPage = 1;
      this.fetchAnnouncements();
    },

    onPageChange() {
      this.fetchAnnouncements();
    },

    async handleMarkAsRead(announcement) {
      try {
        await announcementsApi.markAsRead(announcement.id);
        announcement.is_read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.$notify({
          type: 'success',
          message: 'Announcement marked as read',
          timeout: 2000
        });
      } catch (error) {
        console.error('Failed to mark as read:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to mark as read',
          timeout: 3000
        });
      }
    },

    async handleDismiss(announcement) {
      try {
        await announcementsApi.markAsRead(announcement.id, true);
        // Remove dismissed announcement from the list
        this.announcements = this.announcements.filter(a => a.id !== announcement.id);
        this.totalRows = Math.max(0, this.totalRows - 1);
        if (!announcement.is_read) {
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
        this.$notify({
          type: 'success',
          message: 'Announcement dismissed',
          timeout: 2000
        });
      } catch (error) {
        console.error('Failed to dismiss:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to dismiss announcement',
          timeout: 3000
        });
      }
    },

    async handleMarkAllRead() {
      try {
        await announcementsApi.markAllAsRead();
        this.announcements = this.announcements.map(a => ({ ...a, is_read: true }));
        this.unreadCount = 0;
        this.$notify({
          type: 'success',
          message: 'All announcements marked as read',
          timeout: 3000
        });
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to mark all as read',
          timeout: 3000
        });
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.btn-group-toggle .btn {
  display: inline-flex;
  align-items: center;
}

/* Dark mode (default) */
.announcements-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.announcement-card {
  display: flex;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.announcement-card:hover {
  border-color: rgba(255, 255, 255, 0.7);
}

.announcement-card.expired {
  opacity: 0.7;
}

.priority-border {
  width: 4px;
  flex-shrink: 0;
}

.priority-border.priority-info {
  background: #17a2b8;
}

.priority-border.priority-warning {
  background: #ffc107;
}

.priority-border.priority-critical {
  background: #dc3545;
}

.announcement-body {
  flex: 1;
  padding: 1.25rem;
}

.priority-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-info {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
}

.badge-warning {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.badge-critical {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.expired-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background: rgba(108, 117, 125, 0.2);
  color: #6c757d;
}

.unread-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background: rgba(225, 78, 202, 0.2);
  color: #e14eca;
}

.announcement-title {
  color: #fff;
  margin-bottom: 0;
}

.announcement-content {
  color: rgba(255, 255, 255, 0.8);
}

.announcement-date {
  font-size: 0.85rem;
  font-weight: 400;
  color: #fff;
}

.announcement-actions {
  padding-top: 0.5rem;
}

/* Light mode overrides */
:global(.white-content) .announcement-card {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.1);
}

:global(.white-content) .announcement-card:hover {
  border-color: rgba(0, 0, 0, 0.2);
}

:global(.white-content) .announcement-title {
  color: #344675;
}

:global(.white-content) .announcement-content {
  color: rgba(34, 42, 66, 0.8);
}

:global(.white-content) .announcement-date {
  color: #525f7f;
}

</style>
